import { ILocalizationClient } from "@multiweb/clients/localization/ILocalizationClient";
import { ILocalizationItem } from "@multiweb/types/contracts/localizations";
import { Deferred, PromiseUtils } from "Global/Base/Promises/PromiseUtils";
import { ILocalizationStorage } from "./ILocalizationStorage";
import { LocalizationManager } from "./LocalizationManager";

type ScheduledData = {
    request: Deferred<readonly ILocalizationItem[]>;
    remainingLocKeys: Set<string>;
    localizations: ILocalizationItem[];
};

/**
 * Manager přidává navíc funkcionalitu throttlingu (http://demo.nimius.net/debounce_throttle/), tedy seskupování více dotazů, které vznikly za určitý úsek času.
 * Díky tomu je možné omezit počet dotazů posílaných po síti a dotazů na čtení z lokálního úložiště.
 *
 * První dotaz je vyvolán hned. Pokud v krátném úšeku času po něm vzniknout další dotazy, tak jsou seskupeny a zpracovaný hromadně po skočení daného časového úseku.
 * Časový úsek je konfigurovatelný parametrem konstruktoru.
 */
export class LocalizationManagerThrottled extends LocalizationManager {
    private readonly _throttleTime: number;
    private _lastRequestTime: number = 0;
    private _nextScheduledRunHandle?: number;
    private _scheduledLanguage?: string;
    private _scheduledData: ScheduledData[] = [];

    constructor(throttleTime: number, storage: ILocalizationStorage, client: ILocalizationClient) {
        super(storage, client);
        this._throttleTime = throttleTime;
    }

    public override getLocalizations(language: string, locKeys: readonly string[]): Promise<readonly ILocalizationItem[]> {
        const loading = PromiseUtils.defer<readonly ILocalizationItem[]>();

        try {
            // dotaz se spustí hned, pokud je první po nějaké době a žádný už chvíli neběží
            if (this._canRunRequest()) {
                this._markRunTime();
                loading.resolve(this._getLocalizations(language, locKeys));
            } else {
                this._saveRequestData(language, locKeys, loading);

                if (this._nextScheduledRunHandle === undefined)
                    this._nextScheduledRunHandle = self.setTimeout(() => {
                        this._markRunTime();
                        this._loadBatch();
                        this._reset();
                    }, this._getNextRunIn());
            }
        } catch (e) {
            return Promise.reject(e);
        }

        return loading.promise;
    }

    private _saveRequestData(language: string, locKeys: readonly string[], request: Deferred<readonly ILocalizationItem[]>): void {
        if (this._scheduledLanguage !== undefined && this._scheduledLanguage !== language)
            throw new Error("Simultaneous loading of multiple languages is not supported");

        this._scheduledLanguage = language;

        const requestData: ScheduledData = {
            request,
            remainingLocKeys: this._getUniqueLocKeys(locKeys),
            localizations: [],
        };

        this._scheduledData.push(requestData);
    }

    private async _loadBatch(): Promise<void> {
        if (this._scheduledLanguage === undefined) {
            this._scheduledData.forEach(schedule => schedule.request.reject(new Error("Language not defined")));
            return;
        }

        const scheduledData = this._scheduledData;
        this._scheduledData = [];

        // spojí lokalizační klíče všech requestů a udělá z nich pole unikátních hodnot
        const uniqueLocKeys: string[] = [...scheduledData.reduce((accumulator, scheduleItem) => {
            scheduleItem.remainingLocKeys.forEach(locKey => accumulator.add(locKey));
            return accumulator;
        }, new Set<string>())];

        const onLocalizationsPartLoaded = (localizations: readonly ILocalizationItem[]) => this._processLoadedLocalizations(scheduledData, localizations);
        const loadedLocalizations = await this._getLocalizations(this._scheduledLanguage, uniqueLocKeys, onLocalizationsPartLoaded);

        this._processLoadedLocalizations(scheduledData, loadedLocalizations);

        scheduledData.filter(schedule => schedule.remainingLocKeys.size !== 0).forEach(schedule => schedule.request.reject(new Error("Did not receive localizations for all requested localization keys")));
    }

    private _processLoadedLocalizations(scheduledData: ScheduledData[], localizations: readonly ILocalizationItem[]): void {
        for (const loc of localizations) {
            for (const schedule of scheduledData) {
                if (schedule.remainingLocKeys.has(loc.locKey)) {
                    schedule.remainingLocKeys.delete(loc.locKey);
                    schedule.localizations.push(loc);
                }
            }
        }

        this._resolveCompletedRequests(scheduledData);
    }

    private _resolveCompletedRequests(scheduledData: ScheduledData[]): void {
        scheduledData.filter(schedule => schedule.remainingLocKeys.size === 0).forEach(schedule => schedule.request.resolve(schedule.localizations));
    }

    private _canRunRequest(): boolean {
        return this._lastRequestTime + this._throttleTime <= Date.now();
    }

    private _markRunTime(): void {
        this._lastRequestTime = Date.now();
    }

    private _getNextRunIn(): number {
        return this._lastRequestTime + this._throttleTime - Date.now();
    }

    private _reset(): void {
        this._scheduledLanguage = undefined;
        this._nextScheduledRunHandle = undefined;
    }
}
